import { useState, useEffect, useRef } from 'react'
import { Spinner } from '@heroui/react'
import { useAuthStore } from '../../../store/useAuthStore'
import { checkSessionExpired } from '../../../api/client'
import { WIND_SPEED_INTERVALS, dirsForVtype } from '../../../constants/windrose'
import { ChartLoginPlaceholder, useChartHidden } from './ChartAuthGate'
import { ChartDownloadButton } from './ChartDownloadButton'

// Interaktivní overlay nad windrose SVG grafem — polární obdoba ChartView.jsx.
// Geometrie je kartézská vs. polární, takže se ChartView.jsx nerozšiřuje, je to
// samostatná komponenta. Viz doc/plans/windrose-report.md sekce 4.
function intervalLabelFromSeriesId(id) {
  if (id === 'SUM') return null
  const m = /_(\d\d)$/.exec(id)
  return m ? (WIND_SPEED_INTERVALS[m[1]] || m[1]) : id
}

function fmtPct(value) {
  return value == null ? '—' : `${value.toFixed(1)} %`
}

// Tooltip ukazuje celou výseč — všechny rychlostní intervaly daného směru najednou,
// takže nezáleží, ve kterém kumulativním pásmu je kurzor.
function WindRoseTooltip({ x, y, dirName, rows, total }) {
  return (
    <div
      style={{ position: 'absolute', left: x + 12, top: y + 12, pointerEvents: 'none', zIndex: 10 }}
      className="bg-white border border-gray-200 rounded shadow-md text-xs min-w-[160px]"
    >
      <div className="px-2.5 py-1.5 border-b border-gray-100 font-medium text-gray-700">{dirName}</div>
      <table className="w-full">
        <tbody>
          {rows.map(r => (
            <tr key={r.id}>
              <td className="px-2.5 py-0.5 text-gray-500 whitespace-nowrap">{r.label}</td>
              <td className="px-2.5 py-0.5 text-right tabular-nums text-gray-800 whitespace-nowrap">{fmtPct(r.value)}</td>
            </tr>
          ))}
          {total != null && (
            <tr className="border-t border-gray-100">
              <td className="px-2.5 py-0.5 pb-1.5 pt-1 text-gray-600 font-medium whitespace-nowrap">Suma</td>
              <td className="px-2.5 py-0.5 pb-1.5 pt-1 text-right tabular-nums text-gray-800 font-medium whitespace-nowrap">{fmtPct(total)}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

export function WindRoseInteractive({ url }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [tooltip, setTooltip] = useState(null)
  const wrapperRef = useRef(null)
  const token = useAuthStore(s => s.token)
  const chartHidden = useChartHidden()

  useEffect(() => {
    if (chartHidden) return
    setLoading(true)
    setError(false)
    setTooltip(null)
    setData(null)

    const headers = token ? { Authorization: `Bearer ${token}` } : {}
    fetch(url, { headers })
      .then(r => { if (!r.ok) { checkSessionExpired(r); throw new Error(`HTTP ${r.status}`) }; return r.json() })
      .then(d => { setData(d); setLoading(false) })
      .catch(e => { console.error('[WindRoseInteractive] Chyba:', e); setError(true); setLoading(false) })
  }, [url, token, chartHidden])

  function handleMove(e) {
    if (!data?.meta) return
    const domain = data.meta.svg?.domain
    if (!domain || domain.type !== 'polar') return
    const { center, radius, windDirs, angleOffset } = domain
    const series = data.meta.series || []
    if (series.length === 0) return

    const rect = wrapperRef.current.getBoundingClientRect()
    const mouseX = e.clientX - rect.left
    const mouseY = e.clientY - rect.top

    const dx = mouseX - center.x
    const dy = mouseY - center.y
    const distance = Math.sqrt(dx * dx + dy * dy)

    if (distance > radius) {
      setTooltip(null)
      return
    }

    const angleDeg = ((Math.atan2(dx, -dy) * 180 / Math.PI) - (angleOffset || 0) + 360) % 360
    const step = 360 / windDirs
    const directionIndex = Math.round(angleDeg / step) % windDirs

    const dirNames = dirsForVtype(windDirs === 8 ? 'R08' : 'R16')

    // Celá výseč: všechny rychlostní intervaly daného směru (u neintervalové
    // varianty je série jediná, "SUM" — pak se řádek Suma neopakuje).
    const rows = series.map(s => ({
      id: s.id,
      label: intervalLabelFromSeriesId(s.id) ?? 'Četnost',
      value: s.directions[directionIndex]?.value ?? null,
    }))
    const total = series.length > 1
      ? rows.reduce((sum, r) => sum + (r.value ?? 0), 0)
      : null

    setTooltip({
      x: mouseX,
      y: mouseY,
      dirName: dirNames[directionIndex],
      rows,
      total,
    })
  }

  if (chartHidden) return <ChartLoginPlaceholder />
  if (loading) return (
    <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
      <Spinner size="sm" /> Načítání grafu…
    </div>
  )
  if (error) return <p className="text-red-500 text-sm py-4">Chyba při načítání grafu</p>

  const { svg, meta } = data
  const w = meta?.context?.width || 'auto'
  const h = meta?.context?.height || 'auto'
  const plotArea = meta?.svg?.plotArea

  return (
    <div ref={wrapperRef} style={{ position: 'relative', width: w, height: h, display: 'inline-block' }}>
      {svg && <div dangerouslySetInnerHTML={{ __html: svg }} />}
      <ChartDownloadButton data={data} name="ruzice" />
      {plotArea && (
        <div
          style={{
            position: 'absolute',
            left: plotArea.x,
            top: plotArea.y,
            width: plotArea.width,
            height: plotArea.height,
            cursor: 'crosshair',
          }}
          onMouseMove={handleMove}
          onMouseLeave={() => setTooltip(null)}
        />
      )}
      {tooltip && <WindRoseTooltip {...tooltip} />}
    </div>
  )
}
